import type { IconDefinition } from "@fortawesome/pro-light-svg-icons";
import {
    faCircleExclamation,
    faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { Theme } from "@mui/system";
import type {
    GridColumnHeaderParams,
    GridRenderCellParams,
} from "@mui/x-data-grid";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { ArrowRight } from "@vapor/react-icons";
import { IconButton, Stack, Tooltip } from "@vapor/react-material";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import type { GovernmentAgency } from "../../../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";
import type { TaxSubmissionDTO } from "../../../../../core/api/use-get-tax-submissions/TaxSubmissionDTO";
import { TaxSubmissionErrorType } from "../../../../../core/api/use-get-tax-submissions/TaxSubmissionDTO";
import type { OwnerApp } from "../../../../../core/dtos/OwnerApp";
import { TaxSubmissionStatus } from "../../../../../core/dtos/TaxSubmissionStatus";
import { ValidationDocument } from "../../../../../core/dtos/ValidationDocumentDto";
import { TaxSubmissionStatusChip } from "../../../../components/tax-submission-status-chip/TaxSubmissionStatusChip";

export interface RowModel {
    deadline: string;
    documents: number;
    revoked: string;
    error?: TaxSubmissionErrorType;
    fulfilmentName: string;
    governmentAgency: GovernmentAgency;
    id: string;
    isAgencyDataEditable: boolean;
    isTaxSubmissionDeletable: boolean;
    isRevocable: boolean;
    isSendable: boolean;
    name: string;
    ownerApp?: OwnerApp;
    status: TaxSubmissionStatus;
    type: string;
    sendDate: string;
    validationDocument?: ValidationDocument;
}

export const getRowModel = (taxSubmission: TaxSubmissionDTO): RowModel => ({
    deadline: taxSubmission.fulfilmentSchema.deadline,
    documents: taxSubmission.documentCounters.total,
    revoked:
        taxSubmission.documentCounters.revoked > 0
            ? (taxSubmission.documentCounters.revoked as unknown as string)
            : "-",
    error: taxSubmission.error,
    fulfilmentName: taxSubmission.fulfilmentSchema.name || "",
    governmentAgency: taxSubmission.governmentAgency,
    id: taxSubmission.id,
    isAgencyDataEditable: taxSubmission.isAgencyDataEditable,
    isTaxSubmissionDeletable: taxSubmission.isTaxSubmissionDeletable,
    isRevocable: taxSubmission.isRevocable,
    isSendable: taxSubmission.isSendable,
    name: taxSubmission.fileName,
    ownerApp: taxSubmission.ownerApp,
    status: taxSubmission.status,
    type: taxSubmission.type,
    sendDate: taxSubmission.governmentAgency.sendDate || "",
    validationDocument: taxSubmission.validationDocument,
});

export const renderHeader = (
    theme: Theme,
    params: GridColumnHeaderParams,
    whitespace?: string,
) => (
    <Typography
        color={theme.palette.primary.textTitleColor}
        variant="body500"
        whiteSpace={whitespace}
        noWrap
    >
        {params.colDef.headerName}
    </Typography>
);

export const renderCell = (_: Theme, params: GridRenderCellParams) => (
    <Typography variant="body" noWrap>
        {params.value}
    </Typography>
);

export const renderDateCell = (_: Theme, params: GridRenderCellParams) => (
    <Typography variant="body" noWrap>
        {dayjs(params.value).format("DD/MM/YYYY")}
    </Typography>
);

const ERROR_WARNING_CELL: Record<
    TaxSubmissionErrorType,
    { icon: IconDefinition; label: string; color: string }
> = {
    MISSING_AGENCY_PROTOCOL: {
        icon: faCircleExclamation,
        label: "views.home.components.tax_submissions_table.cell.warnings.missing_agency_protocol.tooltip",
        color: "accentRustyRed",
    },
    VALIDATION: {
        icon: faCircleExclamation,
        label: "views.home.components.tax_submissions_table.cell.warnings.sogei_error.tooltip",
        color: "accentRustyRed",
    },
    TAX_PAYERS: {
        icon: faTriangleExclamation,
        label: "views.home.components.tax_submissions_table.cell.warnings.tax_payers_error.tooltip",
        color: "baseWarningColor",
    },
    OUTCOMES_DOWNLOAD: {
        icon: faTriangleExclamation,
        label: "views.home.components.tax_submissions_table.cell.warnings.outcomes_download_error.tooltip",
        color: "baseWarningColor",
    },
    GENERIC_ERROR: {
        icon: faCircleExclamation,
        label: "views.home.components.tax_submissions_table.cell.warnings.generic_error.tooltip",
        color: "accentRustyRed",
    },
};

export const renderActionsCell = (
    params: GridRenderCellParams<RowModel>,
    onArrowClick: (taxSubmission: string) => unknown,
) => (
    <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={() => onArrowClick(params.row.id)}>
            <ArrowRight color="interactive" />
        </IconButton>
    </Stack>
);

export const renderStatus = (
    theme: Theme,
    params: GridRenderCellParams,
    t: TFunction,
) => {
    const warning =
        ERROR_WARNING_CELL[params.row.error as TaxSubmissionErrorType];

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            {warning ? (
                <Tooltip title={t(warning.label)} placement="top">
                    <FontAwesomeIcon
                        size="lg"
                        icon={warning.icon}
                        color={theme.palette.primary[warning.color]}
                    />
                </Tooltip>
            ) : (
                <FontAwesomeIcon
                    size="lg"
                    icon={faCircleExclamation}
                    style={{ visibility: "hidden" }}
                />
            )}
            <TaxSubmissionStatusChip
                status={params.value}
                variant={
                    params.value === TaxSubmissionStatus.GENERIC_ERROR
                        ? "standard"
                        : "ghost"
                }
            />
        </Stack>
    );
};
